import { useSeo } from "@/lib/seo";
import { trackPage } from "@/lib/track";
import { useEffect } from "react";
import "@/styles/home.css";

const SITE = "https://99tests.com";

const MEANS = [
  ["Real people, real phones.", "Testers run your cases on phones they own: Redmi, Realme, Galaxy, vivo, OPPO, POCO, iPhone. On their own Jio, Airtel and Vi connections, in their own cities."],
  ["Timed, paid sessions.", "Each session is 45 or 90 minutes on one device. Testers are paid per session plus a bonus for every reproducible bug, so they hunt for the bug, not the hour."],
  ["Verified before you see it.", "A separate reviewer checks each finding, merges duplicates and grades severity. Every bug reaches you with steps, device, OS, screenshots and a fix prompt."],
];

const COMPARE = {
  cols: ["99tests crowd", "Device cloud (BrowserStack App Live, AWS Device Farm)", "Managed crowdtesting (uTest, Testlio, Test IO, Global App Testing)"],
  rows: [
    ["Who runs the test", "Indian testers on phones they own", "You, remotely, on a rack device", "A managed community with a project manager"],
    ["How you buy", "Fixed price per cycle, pay by card", "Monthly subscription", "Custom quote, usually annual"],
    ["Time to first report", "3 business days", "Instant access; you do the work", "Days to weeks"],
    ["Test cases", "Yours, in any format; we parse them into sessions", "Yours; you execute them", "Written with you"],
    ["Devices and networks", "Phones Indians own, on Jio, Airtel and Vi", "Rack devices in a data centre", "A global community"],
    ["Best for", "An India release, a fixed budget, AI-generated cases that need running", "Automation suites and debugging", "Multi-country programs, localisation, accessibility"],
  ],
};

const WHO = [
  "20,000 registered testers across India, on the phones and networks your users have.",
  "Testers earn ₹300 per 45-minute session on their own phone, plus ₹100 to ₹500 for every reproducible bug, paid by UPI after your report goes out.",
  "Sessions are offered to testers who own the device you asked for; the first to accept gets it. An unfilled session is credited back to you.",
  "Execution and verification are different people. The tester who found a bug never grades it.",
];

const SAFE = [
  ["You share it by link", "APK, Play internal testing, TestFlight or a staging URL. Only the testers assigned to your sessions can see it."],
  ["Testers accept confidentiality", "Testers accept tester terms, including confidentiality, before their first session."],
  ["Nothing is kept", "Builds and credentials are not kept after the job closes."],
  ["The report is yours", "A private web page and a PDF, emailed to you."],
];

const YEARS = [
  "Founded in Bengaluru in 2010.",
  "300+ companies and 127,000+ bugs reported, including work for Flipkart, Infosys, Wipro and P&G.",
  "Included in Gartner's Market Guide for Crowdsourced Application Testing Services.",
];

const TESTIMONIALS = [
  { quote: "The use of crowd concept emulating the real world delivered a unique insight of our customer's experience.", name: "Steve Coles", title: "CTO, Allianz Insurance", note: "Also an investor in 99tests" },
  { quote: "As a company, we have made great strides in improving our website quality thanks to continued crowdsourced testing with 99tests.", name: "Daniel Yates", title: "Founder, Pitchup" },
  { quote: "99tests has been an invaluable QA partner for us over the last several months, right from Day 1.", name: "Mekin Maheshwari", title: "Chief People Officer, Flipkart" },
];

const PACKAGES = [
  ["Starter", "₹9,999", "$129", "5 × 45-min sessions · 2 devices · up to ~25 cases · report in 3 business days"],
  ["Standard", "₹18,999", "$249", "10 × 45-min sessions · 4 devices · up to ~50 cases · report in 3 business days"],
  ["Deep", "₹34,999", "$449", "20 × 90-min sessions · 6 devices · up to ~120 cases · report in 5 business days"],
];

const FAQ = [
  ["How is this different from beta testing?", "Beta testers use the app and tell you what they think. 99tests testers execute your test cases step by step, log pass or fail with evidence, and a reviewer verifies every failure. If you want both, run a beta and a 99tests cycle on the same build.", null],
  ["Do I have to write the test cases?", "Yes. Send them in any format: a TestRail or Jira export, a spreadsheet, a document, or the output of your AI tool. We parse them into device sessions and show you the plan before testing starts. If you have none yet, the free test case library covers 25 flows Indian apps depend on.", ["Browse the test case library", "/test-cases"]],
  ["Who picks the devices?", "You can name brands, models or a minimum OS, and we match testers who own them. If you do not, we pick the mix your users most likely have in India. Current coverage is on the devices page.", ["See device coverage", "/devices"]],
  ["Can you test a website or only an app?", "Chrome, Safari, Edge and Firefox on Windows, macOS and ChromeOS, on testers' own laptops and desktops. You choose the browser and OS as targets the same way you choose a phone. The session records the actual browser version and screen size, and the report shows them.", null],
  ["Do you invoice with GST?", "Yes, for Indian customers: add your GSTIN at checkout and the invoice carries it. Customers outside India are billed in USD.", null],
  ["How fast is the report?", "Three business days for Starter and Standard, five for Deep. If we miss the report deadline shown on your invoice, get in touch and we'll make it right — the only exception is when your build or test credentials weren't available to our testers.", null],
];

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/pricing">Pricing</a>
        <a href="/about" data-testid="nav-about">About</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <a href="#faq">Questions</a>
        <a className="btn sm blue" href="/pricing" data-testid="crowdsourced-start-job-btn">Start a job</a>
      </nav>
    </div></header>
  );
}

function Footer() {
  return (
    <footer className="site-footer"><div className="wrap">
      <div>© 2026 99tests · Bengaluru<br /><span className="small">Not test automation. Not a certification or an audit under any standard.</span></div>
      <div data-testid="crowdsourced-footer-explore">
        <a href="/crowdsourced-testing">Crowdsourced testing</a>
        <a href="/mobile-app-testing-india">Mobile app testing in India</a>
        <a href="/real-device-testing-india">Real-device testing in India</a>
        <a href="/pricing">Pricing</a>
        <a href="/sample-report/">Sample report</a>
      </div>
      <div>
        <a href="/">Home</a>
        <a href="/join">Become a tester</a>
        <a href="/mcp-server" data-testid="footer-mcp">For AI agents (MCP)</a>
        <a href="/terms">Client terms</a>
        <a href="mailto:hello@99tests.com">hello@99tests.com</a>
      </div>
    </div></footer>
  );
}

export default function CrowdsourcedTesting() {
  const path = "/crowdsourced-testing";
  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "Crowdsourced testing", item: SITE + path },
      ] },
      { "@type": "Service", name: "Crowdsourced testing in India", serviceType: "Crowdsourced software testing", provider: { "@id": "https://99tests.com/#org" }, areaServed: "IN", description: "Real testers across India run your test cases on their own phones and browsers; a reviewer verifies every finding. Fixed price, report in 3 business days." },
      { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ],
  };
  useSeo({
    fullTitle: "Crowdsourced testing in India: 20,000 real testers, fixed price | 99tests",
    description: "Crowdsourced testing since 2010. Real testers run your test cases on their own phones and browsers across India. Fixed price from ₹9,999 / $129, verified report in 3 business days.",
    path,
    jsonld,
    image: "/assets/og-crowdsourced-testing.png",
  });
  useEffect(() => { trackPage(path); }, []);

  return (
    <>
      <Header />
      <main data-testid="crowdsourced-testing">
        <section className="hero">
          <div className="wrap">
            <div>
              <div className="ref"><b>Fixed price</b><span className="dot"></span><b>3 business days</b><span className="dot"></span><span>Phones, laptops, browsers</span></div>
              <h1>Crowdsourced testing on <em>real phones in India.</em></h1>
              <p className="lede">Upload your test cases, pick devices, pay a fixed price. Testers across India run them on their own phones and browsers, a reviewer checks every finding, and you get a pass/fail report in 3 business days. From ₹9,999 + GST, or $129. No account, no sales call.</p>
              <div className="actions">
                <a className="btn blue" href="/pricing" data-testid="crowdsourced-packages-btn">See packages</a>
                <a className="btn ghost" href="/sample-report/" data-testid="crowdsourced-sample-report-link">Read a sample report</a>
              </div>
              <p className="muted small" data-testid="crowdsourced-stats" style={{ marginTop: 10 }}>300+ companies · 127,000+ bugs reported · 20,000 registered testers · since 2010</p>
            </div>
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">What it means</span><h2>What crowdsourced testing means at 99tests.</h2></div>
            <div className="proof" data-testid="crowdsourced-means">
              {MEANS.map(([h, p], i) => (<div key={i}><h4>{h}</h4><p>{p}</p></div>))}
            </div>
            <p className="small" style={{ marginTop: 14, color: "var(--muted)" }}>Not automation, not a bug bounty, not beta feedback. Manual functional and exploratory testing only.</p>
          </div>
        </section>

        <section>
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">How it compares</span><h2>Crowd, device cloud or managed program?</h2></div>
            <div style={{ overflow: "auto" }}>
              <table className="tbl" data-testid="crowdsourced-compare-table">
                <thead><tr><th>{"\u00a0"}</th>{COMPARE.cols.map((c, i) => <th key={i} style={i === 0 ? { color: "var(--ink)" } : null}>{c}</th>)}</tr></thead>
                <tbody>{COMPARE.rows.map((row, ri) => (
                  <tr key={ri}>
                    <td style={{ fontWeight: 600 }}>{row[0]}</td>
                    <td className="small" style={{ fontWeight: 600, color: "var(--ink)" }}>{row[1]}</td>
                    <td className="small" style={{ color: "var(--muted)" }}>{row[2]}</td>
                    <td className="small" style={{ color: "var(--muted)" }}>{row[3]}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <p className="small" style={{ marginTop: 12 }}>Compare: <a href="/vs-device-clouds-and-automation">vs device clouds &amp; automation</a> · <a href="/vs/utest">vs uTest</a> · <a href="/vs/testlio">vs Testlio</a> · <a href="/vs/browserstack-app-live">vs BrowserStack App Live</a> · <a href="/vs/test-io">vs Test IO</a> · <a href="/vs/global-app-testing">vs Global App Testing</a></p>
          </div>
        </section>

        <section className="band">
          <div className="wrap narrow" style={{ marginLeft: 0 }}>
            <div className="section-head"><span className="eyebrow">The crowd</span><h2>Who the crowd is.</h2></div>
            <ul data-testid="crowdsourced-who">{WHO.map((t, i) => <li key={i} style={{ marginBottom: 8 }}>{t}</li>)}</ul>
            <p><a className="btn ghost" href="/join" data-testid="crowdsourced-join-link">Become a tester</a></p>
          </div>
        </section>

        <section>
          <div className="wrap narrow" style={{ marginLeft: 0 }}>
            <div className="section-head"><span className="eyebrow">Confidentiality</span><h2>Is crowdsourced testing safe for an unreleased app?</h2></div>
            <p className="lede">It is the first question every team asks, so here is exactly what happens to your build.</p>
            <div className="proof" data-testid="crowdsourced-safe" style={{ marginTop: 8 }}>
              {SAFE.map(([h, p], i) => (<div key={i}><h4>{h}</h4><p>{i === 1 ? <>Testers accept <a href="/tester-terms">tester terms</a>, including confidentiality, before their first session.</> : p}</p></div>))}
            </div>
          </div>
        </section>

        <section className="band">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Track record</span><h2>Sixteen years of crowdsourced testing.</h2></div>
            <ul data-testid="crowdsourced-years" style={{ marginBottom: 18 }}>{YEARS.map((t, i) => <li key={i} style={{ marginBottom: 8 }}>{t}</li>)}</ul>
            <div className="proof" data-testid="crowdsourced-testimonials">
              {TESTIMONIALS.map((t, i) => (
                <div key={i}><p>&ldquo;{t.quote}&rdquo;</p><p className="muted small"><b>{t.name}</b> — {t.title}{t.note ? ` · ${t.note}` : ""}</p></div>
              ))}
            </div>
            <div className="logos" style={{ marginTop: 16 }}><span>P&amp;G</span><span>Flipkart</span><span>Infosys</span><span>Wipro</span></div>
          </div>
        </section>

        <section id="packages">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Pricing</span><h2>Three packages. One fixed price each.</h2></div>
            <div className="proof" data-testid="crowdsourced-packages">
              {PACKAGES.map(([name, inr, usd, desc], i) => (
                <div key={i}><h4>{name} — {inr} <span className="muted small">/ {usd}</span></h4><p>{desc}</p></div>
              ))}
            </div>
            <p style={{ marginTop: 14 }}><a className="btn blue" href="/pricing">See packages and pay</a></p>
          </div>
        </section>

        <section className="band" id="faq">
          <div className="wrap narrow" style={{ marginLeft: 0 }}>
            <div className="section-head"><span className="eyebrow">Questions</span><h2>Before you pay.</h2></div>
            <div className="faqbox" data-testid="crowdsourced-faq">
              {FAQ.map(([q, a, link], i) => (
                <details className="f" key={i} open={i === 0}><summary>{q}</summary>
                  <p>{a}{link && <> <a href={link[1]}>{link[0]}</a>.</>}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section style={{ borderTop: 0 }}>
          <div className="wrap">
            <div className="cta">
              <div>
                <h2>Try crowdsourced testing on one build.</h2>
                <p>Pay, upload, get a verified report in 3 business days.</p>
              </div>
              <a className="btn" href="/pricing">See packages</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
